import {
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	VStack,
	HStack,
	Text,
	Box,
	Checkbox,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	Spinner,
	Tooltip,
	Icon,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Link
} from '@chakra-ui/react';
import { AlertCircle, AlertTriangle, CheckCircle, ExternalLink, XCircle } from 'lucide-react';
import type { JSX } from 'react';

import { VerifiedQuoteItem, VerifyQuoteItemsSummary } from '@external';
import { formatDealStageLabel } from '@features/crm/quote-verification.helpers';
import { Project } from '@state';

const HUBSPOT_PORTAL_ID = '22371204';

export type QuoteVerificationModalProps = {
	isOpen: boolean;
	onClose: () => void;
	isLoading: boolean;
	verifiedItems: VerifiedQuoteItem[];
	summary: VerifyQuoteItemsSummary | null;
	onConfirm: () => void;
	onCancel: () => void;
	isSubmitting: boolean;
	onPriceSelectionChange?: (index: number, selection: 'hubspot' | 'tool') => void;
	confirmButtonText?: string;
	confirmButtonTextWithIssues?: string;
	quoteName?: string;
	onQuoteNameChange?: (name: string) => void;
	publishQuote?: boolean;
	onPublishQuoteChange?: (value: boolean) => void;
	step?: 'deal-selection' | 'verification';
	deals?: Project[];
	selectedDealId?: string | null;
	onDealSelect?: (dealId: string) => void;
	onContinueToverification?: () => void;
};

const formatCurrency = (value: number | null): string => {
	if (value === null) return 'N/A';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD'
	}).format(value);
};

const getItemTypeLabel = (type: string): string => {
	const labels: Record<string, string> = {
		island: 'Island',
		countertop: 'Countertop',
		backsplash: 'Backsplash',
		appliances: 'Appliance',
		accessories: 'Accessory',
		discount: 'Discount',
		promotion: 'Promotion'
	};
	return labels[type] || type;
};

type PriceToggleProps = {
	value: 'tool' | 'hubspot';
	onChange: (value: 'tool' | 'hubspot') => void;
	hasPriceMismatch?: boolean;
};

const PriceToggle = ({ value, onChange, hasPriceMismatch }: PriceToggleProps): JSX.Element => {
	const isToolSelected = value === 'tool';
	const isHubspotSelected = value === 'hubspot';

	return (
		<Flex
			bg="gray.200"
			borderRadius="full"
			p="2px"
			width="120px"
			height="28px"
			position="relative"
			cursor="pointer"
			transition="all 0.2s"
		>
			{/* Sliding background indicator */}
			<Box
				position="absolute"
				top="2px"
				left={isToolSelected ? '2px' : 'calc(50% + 1px)'}
				width="calc(50% - 3px)"
				height="24px"
				bg={isToolSelected ? 'blue.500' : 'orange.500'}
				borderRadius="full"
				transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
				boxShadow="sm"
			/>

			{/* Tool option */}
			<Flex
				flex="1"
				alignItems="center"
				justifyContent="center"
				zIndex={1}
				onClick={() => onChange('tool')}
				cursor="pointer"
				borderRadius="full"
				transition="all 0.2s"
			>
				<Text
					fontSize="xs"
					fontWeight="semibold"
					color={isToolSelected ? 'white' : 'gray.600'}
					transition="color 0.2s"
					userSelect="none"
				>
					Tool
				</Text>
			</Flex>

			{/* HubSpot option */}
			<Flex
				flex="1"
				alignItems="center"
				justifyContent="center"
				zIndex={1}
				onClick={() => onChange('hubspot')}
				cursor="pointer"
				borderRadius="full"
				transition="all 0.2s"
			>
				<Text
					fontSize="xs"
					fontWeight="semibold"
					color={isHubspotSelected ? 'white' : 'gray.600'}
					transition="color 0.2s"
					userSelect="none"
				>
					HubSpot
				</Text>
			</Flex>
		</Flex>
	);
};

export const QuoteVerificationModal = (props: QuoteVerificationModalProps): JSX.Element => {
	const {
		isOpen,
		onClose,
		isLoading,
		verifiedItems,
		summary,
		onConfirm,
		onCancel,
		isSubmitting,
		onPriceSelectionChange,
		confirmButtonText = 'Create Quote',
		confirmButtonTextWithIssues = 'Proceed Anyway',
		quoteName = '',
		onQuoteNameChange,
		publishQuote = false,
		onPublishQuoteChange,
		step = 'verification',
		deals = [],
		selectedDealId = null,
		onDealSelect,
		onContinueToverification
	} = props;

	const hasIssues = summary?.hasIssues ?? false;
	const missingItems = verifiedItems.filter((item) => !item.isFoundInHubspot);
	const priceMismatchItems = verifiedItems.filter((item) => item.hasPriceMismatch);

	const isDealStep = step === 'deal-selection';

	return (
		<Modal isOpen={isOpen} onClose={onClose} size={isDealStep ? '2xl' : '6xl'} scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxWidth={isDealStep ? undefined : '90vw'} width={isDealStep ? undefined : '1400px'}>
				<ModalHeader>
					<HStack spacing={2}>
						<Text>{isDealStep ? 'Select a Deal' : 'Quote Verification'}</Text>
						{!isDealStep && !isLoading && summary && (
							<Badge colorScheme={hasIssues ? 'orange' : 'green'} fontSize="sm">
								{hasIssues ? 'Issues Found' : 'All Items Verified'}
							</Badge>
						)}
					</HStack>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>{isDealStep ? (
					<VStack spacing={3} align="stretch">
						<Text fontSize="sm" color="gray.600">Select which deal this quote should be assigned to:</Text>
						{deals.map((deal) => (
							<Box
								key={deal.dealId}
								p={4}
								borderWidth="2px"
								borderRadius="md"
								borderColor={selectedDealId === deal.dealId ? 'blue.500' : 'gray.200'}
								bg={selectedDealId === deal.dealId ? 'blue.50' : 'white'}
								cursor="pointer"
								onClick={() => onDealSelect?.(deal.dealId)}
								_hover={{ borderColor: selectedDealId === deal.dealId ? 'blue.500' : 'gray.400' }}
								transition="all 0.15s"
							>
								<HStack justifyContent="space-between">
									<VStack align="start" spacing={1}>
										<Text fontWeight="semibold">{deal.dealName || `Deal ${deal.dealId}`}</Text>
										<HStack spacing={2}>
											<Badge colorScheme="blue" fontSize="xs">{formatDealStageLabel(deal.dealStage)}</Badge>
											<Text fontSize="xs" color="gray.500">ID: {deal.dealId}</Text>
										</HStack>
										{deal.designer && (
											<Text fontSize="xs" color="gray.500">
												Designer: {deal.designer.firstName} {deal.designer.lastName}
											</Text>
										)}
									</VStack>
									<Tooltip label="Open deal in HubSpot" placement="left">
										<Link
											href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${deal.dealId}`}
											isExternal
											display="inline-flex"
											alignItems="center"
											onClick={(e) => e.stopPropagation()}
										>
											<Icon as={ExternalLink} boxSize={4} color="gray.500" _hover={{ color: 'blue.500' }} />
										</Link>
									</Tooltip>
								</HStack>
							</Box>
						))}
					</VStack>
				) : (
					isLoading ? (
						<VStack spacing={4} py={8}>
							<Spinner size="xl" color="blue.500" thickness="4px" />
							<Text color="gray.600">Verifying items against HubSpot...</Text>
						</VStack>
					) : (
						<VStack spacing={4} align="stretch">
							{/* Summary Section */}
							{summary && (
								<Box
									borderWidth="1px"
									borderRadius="md"
									p={4}
									bg={hasIssues ? 'orange.50' : 'green.50'}
								>
									<HStack spacing={6} flexWrap="wrap">
										<VStack align="start" spacing={0}>
											<Text fontSize="sm" color="gray.600">Total Items</Text>
											<Text fontSize="lg" fontWeight="bold">{summary.totalItems}</Text>
										</VStack>
										<VStack align="start" spacing={0}>
											<Text fontSize="sm" color="gray.600">Found in HubSpot</Text>
											<Text fontSize="lg" fontWeight="bold" color="green.600">
												{summary.foundItems}
											</Text>
										</VStack>
										<VStack align="start" spacing={0}>
											<Text fontSize="sm" color="gray.600">Missing</Text>
											<Text
												fontSize="lg"
												fontWeight="bold"
												color={summary.missingItems > 0 ? 'red.600' : 'gray.600'}
											>
												{summary.missingItems}
											</Text>
										</VStack>
										<VStack align="start" spacing={0}>
											<Text fontSize="sm" color="gray.600">Price Mismatch</Text>
											<Text
												fontSize="lg"
												fontWeight="bold"
												color={summary.itemsWithPriceMismatch > 0 ? 'orange.600' : 'gray.600'}
											>
												{summary.itemsWithPriceMismatch}
											</Text>
										</VStack>
									</HStack>
								</Box>
							)}

							{/* Quote Name Input */}
							{onQuoteNameChange && (
								<FormControl>
									<FormLabel fontWeight="medium">Quote Name</FormLabel>
									<Input
										value={quoteName}
										onChange={(e) => onQuoteNameChange(e.target.value)}
										placeholder="Enter quote name"
										size="md"
									/>
									<Text fontSize="xs" color="gray.500" mt={1}>
										This name will appear as the quote title in HubSpot
									</Text>
								</FormControl>
							)}

							{/* Draft vs. Publish */}
							{onPublishQuoteChange && (
								<FormControl>
									<Checkbox
										isChecked={publishQuote}
										onChange={(e) => onPublishQuoteChange(e.target.checked)}
									>
										Publish quote in HubSpot
									</Checkbox>
									<Text fontSize="xs" color="gray.500" mt={1}>
										{publishQuote
											? 'Quote will be published (live) in HubSpot instead of saved as a draft.'
											: 'Quote will be saved as a draft in HubSpot.'}
									</Text>
								</FormControl>
							)}

							{/* Warnings Section */}
							{missingItems.length > 0 && (
								<Alert status="error" borderRadius="md">
									<AlertIcon />
									<Box flex="1">
										<AlertTitle>Missing Items Warning - No HubSpot Product ID</AlertTitle>
										<AlertDescription>
											<Text mb={2}>
												{missingItems.length} item(s) do not have a HubSpot Product ID or were not found in HubSpot&apos;s product catalog.
												These items will NOT be included in the HubSpot quote:
											</Text>
											<Box as="ul" pl={4} fontSize="sm" maxH="100px" overflowY="auto">
												{missingItems.map((item, idx) => (
													<Box as="li" key={`missing-${item.sku}-${idx}`}>
														<Text as="span" fontFamily="mono" fontWeight="medium">{item.sku}</Text>
														<Text as="span" color="gray.600"> ({getItemTypeLabel(item.type)}) - {formatCurrency(item.currentPrice)}</Text>
													</Box>
												))}
											</Box>
										</AlertDescription>
									</Box>
								</Alert>
							)}


							{/* Items Table */}
							<Box overflowX="auto" borderWidth="1px" borderRadius="md">
								<Table size="sm">
									<Thead bg="gray.50">
										<Tr>
											<Th>Status</Th>
											<Th>Type</Th>
											<Th>SKU</Th>
											<Th>Name</Th>
											<Th isNumeric>Qty</Th>
											<Th isNumeric>Tool Price</Th>
											<Th isNumeric>HubSpot Price</Th>
											<Th>Use Price</Th>
										</Tr>
									</Thead>
									<Tbody>
										{verifiedItems.map((item, index) => (
											<Tr
												key={`${item.sku}-${index}`}
												bg={
													!item.isFoundInHubspot
														? 'red.50'
														: item.hasPriceMismatch
															? 'orange.50'
															: undefined
												}
											>
												<Td>
													{!item.isFoundInHubspot ? (
														<Tooltip label="Not found in HubSpot - will be excluded from quote">
															<Box display="inline-flex">
																<Icon as={XCircle} color="red.500" boxSize={5} />
															</Box>
														</Tooltip>
													) : item.hasPriceMismatch ? (
														<Tooltip label="Price differs from HubSpot">
															<Box display="inline-flex">
																<Icon as={AlertTriangle} color="orange.500" boxSize={5} />
															</Box>
														</Tooltip>
													) : (
														<Tooltip label="Verified - matches HubSpot">
															<Box display="inline-flex">
																<Icon as={CheckCircle} color="green.500" boxSize={5} />
															</Box>
														</Tooltip>
													)}
												</Td>
												<Td>
													<Badge
														colorScheme={
															item.type === 'promotion' ? 'purple' :
																item.type === 'island' ? 'blue' :
																	item.type === 'countertop' ? 'teal' :
																		item.type === 'appliances' ? 'cyan' :
																			'gray'
														}
														fontSize="xs"
													>
														{getItemTypeLabel(item.type)}
													</Badge>
												</Td>
												<Td>
													<Text fontSize="xs" fontFamily="mono" maxW="150px" isTruncated>
														{item.sku}
													</Text>
												</Td>
												<Td>
													<Text fontSize="sm" maxW="200px" isTruncated>
														{item.hubspotProductName || '-'}
													</Text>
												</Td>
												<Td isNumeric>{item.quantity}</Td>
												<Td isNumeric>
													<Text fontSize="sm">{formatCurrency(item.currentPrice)}</Text>
												</Td>
												<Td isNumeric>
													<Text
														fontSize="sm"
														color={!item.isFoundInHubspot ? 'red.500' : item.type === 'promotion' ? 'gray.400' : undefined}
													>
														{item.type === 'promotion' ? 'N/A' : formatCurrency(item.hubspotPrice)}
													</Text>
												</Td>
												<Td>
													{item.isFoundInHubspot && onPriceSelectionChange ? (
														<PriceToggle
															value={item.selectedPrice || 'hubspot'}
															onChange={(newValue) => onPriceSelectionChange(index, newValue)}
															hasPriceMismatch={item.hasPriceMismatch}
														/>
													) : (
														<Text fontSize="xs" color="gray.500">
															N/A
														</Text>
													)}
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</Box>

							{/* Confirmation Notice */}
							{hasIssues && (
								<Alert status="info" borderRadius="md">
									<AlertIcon as={AlertCircle} />
									<Box>
										<AlertTitle>Proceed with Caution</AlertTitle>
										<AlertDescription>
											There are issues with some items. Please review the warnings above before proceeding.
											You can still generate the quote, but the issues listed will affect the final HubSpot quote.
										</AlertDescription>
									</Box>
								</Alert>
							)}
						</VStack>
					)
				)}</ModalBody>

				<ModalFooter borderTopWidth="1px">
					<HStack spacing={3}>
						<Button variant="ghost" onClick={onCancel} isDisabled={isSubmitting}>
							Cancel
						</Button>
						{isDealStep ? (
							<Button
								colorScheme="blue"
								onClick={onContinueToverification}
								isDisabled={!selectedDealId}
							>
								Continue
							</Button>
						) : (
							<Button
								colorScheme={hasIssues ? 'orange' : 'green'}
								onClick={onConfirm}
								isDisabled={isLoading || isSubmitting}
								isLoading={isSubmitting}
								loadingText="Creating Quote..."
							>
								{hasIssues ? confirmButtonTextWithIssues : confirmButtonText}
							</Button>
						)}
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
